'use client';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useCallback, useMemo, useContext, useState, type FC } from 'react';
import { Card, Flex, Heading } from '@/_components';
import { DroppableZone } from './components/DroppableZone';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { PreviewDocumentItem } from './components/PreviewDocumentItem';
import { getAllDocumentChecklistItems } from '@/_utils/questionnaire';
import { answerQuestion } from '@/_api/client-questionnaires';
import { markAsOthers } from '@/_api/admin-documents';
import { Question } from '@/_types/models/questionnaire';
import type { DocumentChecklistProps } from './DocumentChecklist.types';
import {
  PREDEFINED_ANSWERS_LIST,
  QUESTION_SEPARATOR,
} from '@/_constants/questions';

const DROPPABLE_ID_ADDITIONAL = 'additional-attachments-container' as const;

export const DocumentChecklist: FC<DocumentChecklistProps> = ({
  additionalAttachments,
  documentsFromAnswers,
  mode,
  loadQuestionnaire,
  questionnaire,
  setDidAtLeastOneDrag,
  setDocumentsFromAnswers,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
  const [loadingSections, setLoadingSections] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  const extractedDocuments = useMemo(
    () =>
      questionnaire?.extracted_documents?.filter(
        (d) => d.status !== 'review_required',
      ) || [],
    [questionnaire?.extracted_documents],
  );
  const allDocumentsNeededAsQuestions =
    getAllDocumentChecklistItems(questionnaire);

  const sourcesOfDocsInAnswers = documentsFromAnswers?.reduce(
    (acc: any, key: any) => {
      const value: any = Object.values(key)[0];
      acc.push(...value);
      return acc;
    },
    [],
  );

  const uploadedAdditionalAttachments = useMemo(
    () =>
      (
        additionalAttachments ||
        extractedDocuments.filter(
          (ed: any) =>
            !sourcesOfDocsInAnswers.some((source: string) =>
              ed.source.includes(source),
            ),
        )
      )
        .filter((document: any) => document.status !== 'split_succeeded')
        .map((document: any) => {
          const list = document.source.split('/');
          // This is assuming a source of something like:
          // /Firm Name/Company Name/Year/etc/etc
          list.splice(0, 3);
          return list.join('/');
        }),
    [additionalAttachments, extractedDocuments, sourcesOfDocsInAnswers],
  );

  const isForClientQuestionnaire: boolean = mode === 'client';

  /* Helper functions for drag handling -------------------------------- */
  const findSourceQuestionId = useCallback(
    (documentName: string): string => {
      /* Check if it's from additional attachments */
      if (uploadedAdditionalAttachments.includes(documentName)) {
        return DROPPABLE_ID_ADDITIONAL;
      }

      /* Check all questions to find which one contains this document */
      for (const question of allDocumentsNeededAsQuestions) {
        const questionData = documentsFromAnswers?.find(
          (hash) => hash[question.id],
        );
        const documents = questionData?.[question.id] || [];

        if (documents.includes(documentName)) {
          return String(question.id);
        }
      }

      return '';
    },
    [
      allDocumentsNeededAsQuestions,
      documentsFromAnswers,
      uploadedAdditionalAttachments,
    ],
  );

  const updateDocumentAnswers = useCallback(
    (documentName: string, oldQuestionId: string, newQuestionId: string) => {
      const ordered = [...documentsFromAnswers];
      const isFromAdditional = oldQuestionId === DROPPABLE_ID_ADDITIONAL;
      const isToAdditional = newQuestionId === DROPPABLE_ID_ADDITIONAL;

      // Dragged from a question droppable zone, remove document from old question's answer
      if (!isFromAdditional) {
        const oldIndex = ordered.findIndex(
          (hash) => Object.keys(hash)[0] === oldQuestionId,
        );
        if (oldIndex >= 0) {
          const questionKey = Number(oldQuestionId);
          const oldDocs = (ordered[oldIndex][questionKey] as string[]).filter(
            (doc: string) => doc !== documentName,
          );
          ordered[oldIndex] = {
            [questionKey]: [...new Set(oldDocs)],
          } as Record<number, string[]>;
        }
      }

      // Dragged to a question droppable zone, add document to new question's answer
      if (!isToAdditional) {
        const newIndex = ordered.findIndex(
          (hash) => Object.keys(hash)[0] === newQuestionId,
        );
        if (newIndex >= 0) {
          const questionKey = Number(newQuestionId);
          const existingDocs =
            (ordered[newIndex][questionKey] as string[]) || [];
          const newDocs = [...existingDocs, documentName];
          ordered[newIndex] = {
            [questionKey]: [...new Set(newDocs)],
          } as Record<number, string[]>;
        }
      }

      return ordered;
    },
    [documentsFromAnswers],
  );

  const processApiUpdates = useCallback(
    async (
      documentName: string,
      oldQuestionId: string,
      newQuestionId: string,
      updatedAnswers: any[],
    ) => {
      const isFromAdditional = oldQuestionId === DROPPABLE_ID_ADDITIONAL;
      const isToAdditional = newQuestionId === DROPPABLE_ID_ADDITIONAL;
      const idOfDocMovedByUser = extractedDocuments.find((ed) =>
        ed.source.includes(documentName),
      )?.id;

      // @Lisia TODO: Merge this function with addUploadedItem in answers.ts
      const buildAnswerString = (answers: string[]) => {
        if (
          !!answers.length &&
          PREDEFINED_ANSWERS_LIST.some((predefinedAnswer) =>
            answers.includes(predefinedAnswer),
          )
        ) {
          const filteredAnswers = answers.filter(
            (answer) => !PREDEFINED_ANSWERS_LIST.includes(answer as any),
          );
          return filteredAnswers.length > 0
            ? QUESTION_SEPARATOR + filteredAnswers.join(QUESTION_SEPARATOR)
            : '';
        } else if (!answers.length) {
          return '';
        } else {
          return QUESTION_SEPARATOR + answers.join(QUESTION_SEPARATOR);
        }
      };

      const updateDocumentAsOthers = async (marked: boolean) => {
        const sourceDoc = extractedDocuments?.find(
          (doc) => doc.source.split('/').slice(3).join('/') === documentName,
        );
        if (!sourceDoc) return;

        const matchedDoc = isFromAdditional
          ? extractedDocuments
              .filter((d) => d.marked_as_others)
              .find((d) => d.source === sourceDoc.source)
          : extractedDocuments.find((d) => d.source === sourceDoc.source);

        if (matchedDoc) {
          await markAsOthers({
            token,
            extractedDocumentId: matchedDoc.id,
            markedAsOthers: marked,
            companyId: questionnaire.company_id,
          });
        }
      };

      const promises = [];

      /* Dragged to a question droppable zone, update the question's answer */
      if (!isToAdditional) {
        const newAnswers =
          updatedAnswers.find(
            (hash) => Object.keys(hash)[0] === newQuestionId,
          )?.[newQuestionId] || [];
        const answerBy = allDocumentsNeededAsQuestions.find(
          (q) => q.id.toString() === newQuestionId,
        )?.answer_by;

        promises.push(
          answerQuestion({
            companyId: questionnaire.company_id,
            answer: buildAnswerString(newAnswers),
            idOfDocMovedByUser,
            questionId: Number(newQuestionId),
            answerBy,
            token,
          }),
        );
      } else {
        promises.push(updateDocumentAsOthers(true));
      }

      /* Handle source updates */
      if (!isFromAdditional) {
        const oldAnswers =
          updatedAnswers.find(
            (hash) => Object.keys(hash)[0] === oldQuestionId,
          )?.[oldQuestionId] || [];
        const answerBy = allDocumentsNeededAsQuestions.find(
          (q) => q.id.toString() === oldQuestionId,
        )?.answer_by;

        promises.push(
          answerQuestion({
            companyId: questionnaire.company_id,
            answer: buildAnswerString(oldAnswers),
            idOfDocMovedByUser,
            questionId: Number(oldQuestionId),
            answerBy,
            token,
          }),
        );
      } else {
        promises.push(updateDocumentAsOthers(false));
      }

      await Promise.all(promises);
    },
    [extractedDocuments, allDocumentsNeededAsQuestions, questionnaire, token],
  );

  /* Helper functions for drag handling -------------------------------- */
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !questionnaire) return;

      const documentName = active.id as string;
      const newQuestionId = over.id as string;
      const oldQuestionId = findSourceQuestionId(documentName) as string;

      setLoadingItemId(newQuestionId);
      setLoadingSections([oldQuestionId, newQuestionId]);

      // No change needed if same location or source not found
      if (!oldQuestionId || oldQuestionId === newQuestionId) {
        setLoadingItemId(null);
        setLoadingSections([]);
        return;
      }

      try {
        setDidAtLeastOneDrag(true);

        // Update local state
        const updatedAnswers = updateDocumentAnswers(
          documentName,
          oldQuestionId,
          newQuestionId,
        );
        setDocumentsFromAnswers(updatedAnswers);

        // Process API updates first, then reload questionnaire to refresh stage
        await processApiUpdates(
          documentName,
          oldQuestionId,
          newQuestionId,
          updatedAnswers,
        );
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoadingItemId(null);
        await loadQuestionnaire();
        setLoadingSections([]);
      }
    },
    [
      questionnaire,
      findSourceQuestionId,
      updateDocumentAnswers,
      processApiUpdates,
      setDidAtLeastOneDrag,
      setDocumentsFromAnswers,
      addToast,
      loadQuestionnaire,
    ],
  );

  return (
    <Flex isColumn={true} gap={4}>
      {!isForClientQuestionnaire && (
        <Heading level={5}>Document Checklist</Heading>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {/* Documents Required From Questions Section */}
        {allDocumentsNeededAsQuestions.map((question: Question) => {
          const questionFromDocumentsFromAnswers = documentsFromAnswers?.find(
            (hash: any) => hash[question.id],
          );
          const documentsFromAnswer: any = questionFromDocumentsFromAnswers
            ? questionFromDocumentsFromAnswers[question.id]
            : [];
          const isLoading = loadingItemId === String(question.id);

          return (
            <DroppableZone
              key={question.id}
              id={String(question.id)}
              isDropDisabled={isLoading}
            >
              <Card padding={4} gap={4}>
                <PreviewDocumentItem
                  documentsFromAnswer={documentsFromAnswer}
                  droppablePlaceholder={null}
                  isAdditionalAttachments={false}
                  isLoading={isLoading}
                  mode={mode}
                  loadQuestionnaire={loadQuestionnaire}
                  question={question}
                  questionnaire={questionnaire!}
                  setLoadingItemId={setLoadingItemId}
                  itemId={String(question.id)}
                  loadingSections={loadingSections}
                />
              </Card>
            </DroppableZone>
          );
        })}
        {/* Additional Attachments Section */}
        <DroppableZone
          id={DROPPABLE_ID_ADDITIONAL}
          isDropDisabled={loadingItemId === DROPPABLE_ID_ADDITIONAL}
        >
          <Card padding={4} gap={4}>
            <PreviewDocumentItem
              droppablePlaceholder={null}
              isAdditionalAttachments
              isLoading={loadingItemId === DROPPABLE_ID_ADDITIONAL}
              mode={mode}
              loadQuestionnaire={loadQuestionnaire}
              questionnaire={questionnaire!}
              setLoadingItemId={setLoadingItemId}
              itemId="additional-attachments"
              uploadedAdditionalAttachments={uploadedAdditionalAttachments}
            />
          </Card>
        </DroppableZone>
      </DndContext>
    </Flex>
  );
};
